/**
 * `/api/task-attachments/**` and `/api/custom-tasks/**` — which catalog/custom tasks each
 * character or account tracks. See `TaskDtos` / `TaskAttachmentController` / `CustomTaskController`.
 *
 * `PATCH /api/custom-tasks/{id}` (WP6 backend addendum, closing WP5's D-API3) edits a custom
 * task's own name/cadence/scope/maxCount; no UI hangs off it yet in this WP, only the client
 * helper (`useUpdateCustomTask`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend } from './api';

export interface TaskAttachmentDto {
  id: number;
  ownerType: 'CHARACTER' | 'ACCOUNT';
  characterId: number | null;
  accountId: number | null;
  source: 'CATALOG' | 'CUSTOM';
  taskDefSlug: string | null;
  customTaskId: number | null;
  name: string;
  cadence: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  scope: 'CHARACTER' | 'ACCOUNT' | 'WORLD';
  maxCount: number;
  enabled: boolean;
  note: string | null;
}

export interface CustomTaskDto {
  id: number;
  name: string;
  cadence: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  scope: 'CHARACTER' | 'ACCOUNT' | 'WORLD';
  maxCount: number;
}

export interface AttachTaskInput {
  taskDefSlug?: string;
  customTaskId?: number;
  characterId?: number;
  accountId?: number;
  enabled?: boolean;
}

export interface CustomTaskInput {
  name: string;
  cadence: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  scope: 'CHARACTER' | 'ACCOUNT' | 'WORLD';
  maxCount: number;
}

/** Undefined fields are left unchanged server-side (see `TaskDtos.UpdateCustomTaskRequest`). */
export type UpdateCustomTaskInput = Partial<CustomTaskInput>;

export const ATTACHMENTS_QUERY_KEY = ['task-attachments'] as const;
export const CUSTOM_TASKS_QUERY_KEY = ['custom-tasks'] as const;

export const attachmentsQuery = {
  queryKey: ATTACHMENTS_QUERY_KEY,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiGet<TaskAttachmentDto[]>('/api/task-attachments', signal),
};

export const customTasksQuery = {
  queryKey: CUSTOM_TASKS_QUERY_KEY,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<CustomTaskDto[]>('/api/custom-tasks', signal),
};

/** All of the user's attachments, across every account/character (client-side filtered from there). */
export function useTaskAttachments() {
  return useQuery(attachmentsQuery);
}

export function useCustomTasks() {
  return useQuery(customTasksQuery);
}

function useInvalidateTaskState() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ATTACHMENTS_QUERY_KEY });
    // Enabling/disabling a task changes what GET /api/checklist returns.
    void queryClient.invalidateQueries({ queryKey: ['checklist'] });
  };
}

export function useAttachTask() {
  const invalidate = useInvalidateTaskState();
  return useMutation({
    mutationFn: (input: AttachTaskInput) => apiSend<TaskAttachmentDto>('POST', '/api/task-attachments', input),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAttachment() {
  const invalidate = useInvalidateTaskState();
  return useMutation({
    mutationFn: ({ attachmentId, enabled }: { attachmentId: number; enabled: boolean }) =>
      apiSend<TaskAttachmentDto>('PATCH', `/api/task-attachments/${attachmentId}`, { enabled }),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteAttachment() {
  const invalidate = useInvalidateTaskState();
  return useMutation({
    mutationFn: (attachmentId: number) => apiSend<void>('DELETE', `/api/task-attachments/${attachmentId}`),
    onSuccess: () => invalidate(),
  });
}

export function useCreateCustomTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomTaskInput) => apiSend<CustomTaskDto>('POST', '/api/custom-tasks', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CUSTOM_TASKS_QUERY_KEY }),
  });
}

export function useUpdateCustomTask() {
  const invalidate = useInvalidateTaskState();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customTaskId, input }: { customTaskId: number; input: UpdateCustomTaskInput }) =>
      apiSend<CustomTaskDto>('PATCH', `/api/custom-tasks/${customTaskId}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CUSTOM_TASKS_QUERY_KEY });
      invalidate();
    },
  });
}

export function useDeleteCustomTask() {
  const invalidate = useInvalidateTaskState();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (customTaskId: number) => apiSend<void>('DELETE', `/api/custom-tasks/${customTaskId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CUSTOM_TASKS_QUERY_KEY });
      invalidate();
    },
  });
}
